import React, { useState, useEffect, useRef, useCallback } from 'react';
import { parseFrDateToIso, formatIsoDateToFr } from '../../utils/dateFormatFr';
import Icon from '../Icon/Icon';
import '../Input/Input.scss';
import './DateInput.scss';

const PLACEHOLDER_FR = 'dd/mm/yyyy';

export interface DateInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'value' | 'onChange'
  > {
  value: string;
  onChange: (isoDate: string) => void;
  label?: string;
  error?: string;
  fullWidth?: boolean;
  /** Sans bloc label / marge, pour l’intégrer dans des layouts existants */
  bare?: boolean;
  compact?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  label,
  error,
  fullWidth = false,
  bare = false,
  compact = false,
  min,
  max,
  placeholder,
  className = '',
  id,
  name,
  disabled,
  required,
  autoFocus,
  onBlur: onBlurProp,
  ...rest
}) => {
  const [text, setText] = useState(() => formatIsoDateToFr(value ?? ''));
  const nativeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(formatIsoDateToFr(value ?? ''));
  }, [value]);

  const inMinMax = (iso: string): boolean => {
    if (!iso) return true;
    const d = new Date(`${iso}T12:00:00`);
    if (min) {
      const minD = new Date(`${min}T12:00:00`);
      if (d < minD) return false;
    }
    if (max) {
      const maxD = new Date(`${max}T12:00:00`);
      if (d > maxD) return false;
    }
    return true;
  };

  const applyIso = useCallback(
    (iso: string) => {
      if (!iso) {
        onChange('');
        setText('');
        return;
      }
      if (!inMinMax(iso)) return;
      onChange(iso);
      setText(formatIsoDateToFr(iso));
    },
    [onChange, min, max]
  );

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    const t = next.trim();
    if (!t) {
      onChange('');
      return;
    }
    const iso = parseFrDateToIso(t);
    if (iso !== null && iso !== '' && inMinMax(iso)) {
      onChange(iso);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlurProp?.(e);
    const t = text.trim();
    if (!t) {
      onChange('');
      setText('');
      return;
    }
    const iso = parseFrDateToIso(t);
    if (iso === null || iso === '') {
      setText(formatIsoDateToFr(value ?? ''));
      return;
    }
    if (!inMinMax(iso)) {
      setText(formatIsoDateToFr(value ?? ''));
      return;
    }
    onChange(iso);
    setText(formatIsoDateToFr(iso));
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyIso(e.target.value || '');
  };

  const openCalendar = () => {
    const el = nativeRef.current;
    if (!el || disabled) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.click();
      }
    } catch {
      el.click();
    }
  };

  const inputId = id || `date-input-${label ? label.toLowerCase().replace(/\s/g, '-') : 'field'}`;

  const innerClassName = bare
    ? `date-input-fr ${className}`.trim()
    : `input-group__input date-input-fr ${error ? 'input-group__input--error' : ''}`.trim();

  const field = (
    <div className={`date-input-fr-wrap ${bare ? 'date-input-fr-wrap--bare' : ''} ${compact ? 'date-input-fr-wrap--compact' : ''}`.trim()}>
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={bare ? id : inputId}
        name={name}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        className={innerClassName}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        placeholder={placeholder ?? PLACEHOLDER_FR}
      />
      <button
        type="button"
        className="date-input-fr-wrap__trigger"
        onClick={openCalendar}
        disabled={disabled}
        aria-label="Ouvrir le calendrier"
      >
        <Icon name="calendar" size={14} />
      </button>
      <input
        ref={nativeRef}
        type="date"
        className="date-input-fr-wrap__native"
        tabIndex={-1}
        aria-hidden
        value={value || ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={handleNativeChange}
      />
    </div>
  );

  if (bare) {
    return field;
  }

  return (
    <div className={`input-group ${fullWidth ? 'input-group--full-width' : ''} ${compact ? 'input-group--compact' : ''} ${className}`.trim()}>
      {label && (
        <label htmlFor={inputId} className="input-group__label">
          {label}
          {required && <span className="input-group__required">*</span>}
        </label>
      )}
      {field}
      {error && <span className="input-group__error">{error}</span>}
    </div>
  );
};

export default DateInput;
